import { useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { AppImage } from "@/components/personal-apps/AppImage";
import type { ReactNode } from "react";

export type AppCardVisualProps = {
  title: string;
  description?: string | null;
  thumbnailUrl?: string | null;
  iconUrl?: string | null;
  fallbackKey?: string;
  objectPosition?: string;
  eagerImage?: boolean;
  descriptionClassName?: string;
  /** Stands in for the thumbnail, e.g. a playing recording. */
  media?: ReactNode;
  mediaOverlay?: ReactNode;
  trailing?: ReactNode;
};

/**
 * The shared visual layer for media-first App cards.
 *
 * Interaction stays with the caller: Store cards add social actions and
 * hover disclosure, while the signed-out landing page makes the whole visual
 * one link-like button. Keeping this component presentational prevents those
 * behaviors from leaking between surfaces.
 */
export default function AppCardVisual({
  title,
  description,
  thumbnailUrl,
  iconUrl,
  fallbackKey = title,
  objectPosition = "center",
  eagerImage,
  descriptionClassName,
  media,
  mediaOverlay,
  trailing,
}: AppCardVisualProps) {
  useLocale();
  const thumbnail = thumbnailUrl?.trim() ?? "";
  const icon = iconUrl?.trim() ?? "";

  return (
    <>
      <div className="gallery-card-media">
        {media ?? (thumbnail ? (
          <img
            className="gallery-card-thumb"
            src={thumbnail}
            alt=""
            style={{ objectPosition }}
            loading={eagerImage ? "eager" : "lazy"}
          />
        ) : (
          <span className="gallery-card-thumb gallery-card-thumb--fallback">
            <AppImage url={icon} size={48} eager={eagerImage} />
            <span className="gallery-card-fallback-preview" aria-hidden><Message message={"{{value0}}\n✓ capsule ready"} values={{ value0: `$ ato run ${fallbackKey}` }} /></span>
          </span>
        ))}
        {mediaOverlay}
      </div>

      <div className="gallery-card-content">
        <div className="gallery-card-info">
          <span className="gallery-card-app-icon" aria-hidden="true">
            <AppImage url={icon} size={24} eager={eagerImage} />
          </span>
          <div className="gallery-card-identity">
            <span className="gallery-card-name">{title}</span>
            {description ? (
              <p
                className={
                  descriptionClassName
                    ? `gallery-card-copy ${descriptionClassName}`
                    : "gallery-card-copy"
                }
              >
                {description}
              </p>
            ) : null}
          </div>
        </div>
        {trailing}
      </div>
    </>
  );
}
